import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import * as GoodsTagsApi from '../api/GoodsTags';
import { GoodsTag } from '../api/GoodsTags';

const formatImportResult = (res: GoodsTagsApi.ImportCsvResult) => {
  const missing =
    res.missing_articles.length > 0
      ? ` Не найдены артикулы: ${res.missing_articles.slice(0, 20).join(', ')}${
          res.missing_articles.length > 20 ? '…' : ''
        }`
      : '';
  return `Строк ${res.rows}, новых тегов ${res.created_tags}, привязок ${res.linked}.${missing}`;
};

const Tags = () => {
  const [tags, setTags] = useState<GoodsTag[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [articlePaste, setArticlePaste] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [linkingTagId, setLinkingTagId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setPending(true);
    try {
      setTags(await GoodsTagsApi.getAll());
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    setError('');
    setMessage('');
    try {
      await GoodsTagsApi.create(name);
      setName('');
      setShowNew(false);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка создания');
    }
  };

  const handleUnlink = async (tag: GoodsTag) => {
    if (
      !window.confirm(
        `Отвязать тег «${tag.name}» от всех товаров (${tag.goods_count ?? 0})?`
      )
    ) {
      return;
    }
    setError('');
    setMessage('');
    try {
      const res = await GoodsTagsApi.unlinkAll(tag.id);
      setMessage(`Отвязано: ${res.unlinked}`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка отвязки');
    }
  };

  const handleDelete = async (tag: GoodsTag) => {
    if (
      !window.confirm(
        `Удалить тег «${tag.name}» из каталога? Связи с товарами тоже будут удалены.`
      )
    ) {
      return;
    }
    setError('');
    setMessage('');
    try {
      const res = await GoodsTagsApi.remove(tag.id);
      setMessage(`Удалён тег, отвязано карточек: ${res.unlinked}`);
      if (editingTagId === tag.id) {
        setEditingTagId(null);
        setArticlePaste('');
      }
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка удаления');
    }
  };

  const handleImport = async (file: File | null) => {
    if (!file) return;
    setError('');
    setMessage('');
    try {
      const res = await GoodsTagsApi.importCsv(file);
      setMessage(`Импорт: ${formatImportResult(res)}`);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка импорта');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleImportText = async () => {
    setError('');
    setMessage('');
    try {
      const res = await GoodsTagsApi.importCsvText(csvText);
      setMessage(`Импорт: ${formatImportResult(res)}`);
      setCsvText('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка импорта');
    }
  };

  const toggleEdit = (tag: GoodsTag) => {
    setError('');
    setMessage('');
    if (editingTagId === tag.id) {
      setEditingTagId(null);
      setArticlePaste('');
      return;
    }
    setEditingTagId(tag.id);
    setArticlePaste('');
  };

  const handleLinkArticles = async (tag: GoodsTag) => {
    setError('');
    setMessage('');
    setLinkingTagId(tag.id);
    try {
      const res = await GoodsTagsApi.linkTagToArticles(tag.name, articlePaste);
      setMessage(`Тег «${tag.name}»: ${formatImportResult(res)}`);
      setArticlePaste('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка привязки');
    } finally {
      setLinkingTagId(null);
    }
  };

  return (
    <Container>
      <Row>
        <Col>
          <Button
            style={{ margin: '16px 8px 16px 0' }}
            onClick={() => {
              setShowNew(!showNew);
              setError('');
            }}
          >
            {!showNew ? 'Создать тег' : 'Скрыть форму'}
          </Button>
          <Button
            variant="outline-primary"
            style={{ margin: '16px 0' }}
            onClick={() => fileRef.current?.click()}
          >
            Загрузить CSV
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => handleImport(e.target.files?.[0] || null)}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Form.Group>
            <Form.Label>Импорт CSV списком</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={'A123,promo,sale\nB456;promo'}
            />
          </Form.Group>
          <Form.Text muted style={{ display: 'block', marginBottom: 8 }}>
            1-й столбец — артикул, остальные — теги (через запятую или точку с
            запятой). Привязка аддитивная.
          </Form.Text>
          <Button
            variant="primary"
            disabled={!csvText.trim()}
            onClick={handleImportText}
          >
            Импортировать из текста
          </Button>
        </Col>
      </Row>
      {showNew && (
        <Row style={{ marginBottom: 16, padding: 16, border: '1px solid #ddd' }}>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Название тега</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: promo"
              />
            </Form.Group>
            <Button
              style={{ marginTop: 8 }}
              disabled={!name.trim()}
              onClick={handleCreate}
            >
              Создать
            </Button>
          </Col>
        </Row>
      )}
      {(error || message) && (
        <Row style={{ marginBottom: 8 }}>
          <Col>
            {error && <div style={{ color: '#c00' }}>{error}</div>}
            {message && <div style={{ color: '#090' }}>{message}</div>}
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          {pending && <div>Загрузка...</div>}
          {!pending &&
            tags.map((tag) => (
              <div key={tag.id} style={{ marginBottom: 8 }}>
                <Row
                  style={{
                    padding: 12,
                    border: '1px solid #eee',
                    backgroundColor: '#f9f9f9',
                    alignItems: 'center',
                  }}
                >
                  <Col md={5}>
                    <strong>{tag.name}</strong>
                  </Col>
                  <Col md={2}>товаров: {tag.goods_count ?? 0}</Col>
                  <Col
                    md={5}
                    style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
                  >
                    <Button
                      size="sm"
                      variant={
                        editingTagId === tag.id
                          ? 'secondary'
                          : 'outline-primary'
                      }
                      onClick={() => toggleEdit(tag)}
                    >
                      {editingTagId === tag.id ? 'Скрыть' : 'Привязать'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleUnlink(tag)}
                      disabled={!tag.goods_count}
                    >
                      Отвязать от всех
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(tag)}
                    >
                      Удалить
                    </Button>
                  </Col>
                </Row>
                {editingTagId === tag.id && (
                  <Row
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      borderTop: 'none',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Col>
                      <Form.Group>
                        <Form.Label>
                          Артикулы для тега «{tag.name}»
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={articlePaste}
                          onChange={(e) => setArticlePaste(e.target.value)}
                          placeholder="Вставка списком: пробел, запятая, точка с запятой или новая строка"
                        />
                      </Form.Group>
                      <Form.Text muted style={{ display: 'block', marginBottom: 8 }}>
                        Список будет распознан и привязан к карточкам. Уже
                        существующие связи сохраняются.
                      </Form.Text>
                      <Button
                        size="sm"
                        disabled={
                          !articlePaste.trim() || linkingTagId === tag.id
                        }
                        onClick={() => handleLinkArticles(tag)}
                      >
                        {linkingTagId === tag.id
                          ? 'Привязка…'
                          : 'Привязать к товарам'}
                      </Button>
                    </Col>
                  </Row>
                )}
              </div>
            ))}
          {!pending && !tags.length && <div>Нет созданных тегов</div>}
        </Col>
      </Row>
    </Container>
  );
};

export default Tags;
